import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, GraduationCap, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray50: '#F9FAFB', gray100: '#F3F4F6',
  gray200: '#E5E7EB', gray400: '#9CA3AF', gray500: '#6B7280',
  gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error:   '#991B1B', errorBg:   '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info:    '#1E40AF', infoBg:    '#DBEAFE',
};

const cardShadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';

const T = {
  ms: {
    title: 'Profil Pendidik', total: 'rekod',
    searchPh: 'Cari nama, ID, e-mel…',
    allStatus: 'Semua Status', allType: 'Semua Jenis',
    prev: 'Sebelum', next: 'Seterusnya',
    showing: 'Menunjukkan', to: 'hingga', of: 'daripada', records: 'rekod',
    emptyTitle: 'Tiada Pendidik Dijumpai',
    emptyBody: 'Tiada rekod pendidik dijumpai. Cuba tukar penapis.',
    errLabel: 'Gagal memuatkan pendidik',
    retry: 'Cuba Lagi',
    sped: 'SpEd',
  },
  en: {
    title: 'Educator Profiles', total: 'records',
    searchPh: 'Search name, ID, email…',
    allStatus: 'All Status', allType: 'All Types',
    prev: 'Previous', next: 'Next',
    showing: 'Showing', to: 'to', of: 'of', records: 'records',
    emptyTitle: 'No Educators Found',
    emptyBody: 'No educator records found. Try adjusting your filters.',
    errLabel: 'Failed to load educators',
    retry: 'Retry',
    sped: 'SpEd',
  },
};

const STATUS_BADGE: Record<string, { bg: string; text: string; label: { ms: string; en: string } }> = {
  ACTIVE:    { bg: '#DCFCE7', text: '#166534', label: { ms: 'Aktif',     en: 'Active' } },
  INACTIVE:  { bg: '#F3F4F6', text: '#4B5563', label: { ms: 'Tidak Aktif', en: 'Inactive' } },
  SUSPENDED: { bg: '#FEF3C7', text: '#92400E', label: { ms: 'Digantung', en: 'Suspended' } },
  RETIRED:   { bg: '#DBEAFE', text: '#1E40AF', label: { ms: 'Bersara',   en: 'Retired' } },
};
const EMP_TYPE: Record<string, { ms: string; en: string }> = {
  PERMANENT: { ms: 'Tetap',        en: 'Permanent' },
  CONTRACT:  { ms: 'Kontrak',      en: 'Contract' },
  SECONDED:  { ms: 'Dipinjamkan',  en: 'Seconded' },
  TEMP:      { ms: 'Sementara',    en: 'Temporary' },
  PART_TIME: { ms: 'Separuh Masa', en: 'Part-time' },
};

interface Props { user: AuthUser }
interface Educator {
  id: string; service_number: string;
  full_name_en: string | null; full_name_ms: string | null; preferred_name: string | null;
  designation: string | null; employment_type: string; employment_status: string;
  career_band: string | null; school_name: string | null;
  email: string | null; photo_url: string | null; is_sped_certified: boolean;
}
interface PageResult { data: Educator[]; total: number; page: number; limit: number }

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && !r.schoolId)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

const selectStyle: React.CSSProperties = {
  backgroundColor: C.white, border: `1.5px solid ${C.border}`,
  borderRadius: '8px', color: C.gray900, padding: '9px 12px',
  fontSize: '14px', outline: 'none', cursor: 'pointer',
  fontFamily: '"Inter", sans-serif', width: '100%',
};

export function EducatorsPage({ user }: Props) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const UI = T[lang === 'en' ? 'en' : 'ms'];
  usePageTitle(lang === 'en' ? 'Educator Profiles' : 'Profil Pendidik');

  const schoolId = getSchoolId(user);

  const [data,    setData]    = useState<Educator[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [searchInput,  setSearchInput]  = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType,   setFilterType]   = useState('');

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (filterSearch.trim()) params.set('search', filterSearch.trim());
      if (filterStatus)        params.set('status', filterStatus);
      if (filterType)          params.set('employmentType', filterType);
      if (schoolId)            params.set('schoolId', schoolId);
      const result = await apiGet<PageResult>(`/api/educators?${params}`);
      setData(result.data as Educator[]);
      setTotal(result.total);
      setPage(result.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : lang === 'en' ? 'Unknown error.' : 'Ralat tidak diketahui.');
    } finally { setLoading(false); }
  }, [filterSearch, filterStatus, filterType, schoolId, lang]);

  useEffect(() => { void load(1); }, [load]);

  const totalPages = Math.ceil(total / 20) || 1;
  const from = Math.min((page - 1) * 20 + 1, total);
  const to   = Math.min(page * 20, total);
  const hasFilters = !!(filterSearch || filterStatus || filterType);

  function clearFilters() {
    setSearchInput(''); setFilterSearch(''); setFilterStatus(''); setFilterType('');
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: C.gray900, marginBottom: '4px', fontFamily: '"Noto Sans", sans-serif' }}>
            {UI.title}
          </h1>
          <p style={{ fontSize: '14px', color: C.gray500 }}>
            {total.toLocaleString()} {UI.total}
          </p>
        </div>
        <button
          onClick={() => void load(page)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: C.white, border: `1px solid ${C.border}`,
            borderRadius: '8px', padding: '8px 14px',
            fontSize: '13px', color: C.gray700, fontWeight: 600,
            cursor: 'pointer', fontFamily: '"Inter", sans-serif',
          }}
        >
          <RefreshCw size={14} strokeWidth={2} />
          {lang === 'en' ? 'Refresh' : 'Muat Semula'}
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          backgroundColor: C.white, border: `1px solid ${C.border}`,
          borderRadius: '12px', padding: '16px',
          boxShadow: cardShadow, marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Search */}
          <div style={{ flex: '2 1 220px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '6px' }}>
              {lang === 'en' ? 'Search' : 'Carian'}
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={15} color={C.gray400} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                value={searchInput}
                placeholder={UI.searchPh}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setFilterSearch(searchInput.trim())}
                disabled={loading}
                style={{ ...selectStyle, paddingLeft: '36px' }}
              />
            </div>
          </div>

          {/* Status filter */}
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '6px' }}>
              {lang === 'en' ? 'Status' : 'Status'}
            </label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading} style={selectStyle}>
              <option value="">{UI.allStatus}</option>
              {Object.entries(STATUS_BADGE).map(([v, cfg]) => (
                <option key={v} value={v}>{lang === 'en' ? cfg.label.en : cfg.label.ms}</option>
              ))}
            </select>
          </div>

          {/* Type filter */}
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '6px' }}>
              {lang === 'en' ? 'Employment Type' : 'Jenis Perjawatan'}
            </label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} disabled={loading} style={selectStyle}>
              <option value="">{UI.allType}</option>
              {Object.entries(EMP_TYPE).map(([v, l]) => (
                <option key={v} value={v}>{lang === 'en' ? l.en : l.ms}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setFilterSearch(searchInput.trim())}
              disabled={loading}
              style={{
                backgroundColor: C.yellow, color: C.black,
                border: 'none', borderRadius: '8px', padding: '9px 16px',
                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              <Search size={14} strokeWidth={2.5} />
              {lang === 'en' ? 'Search' : 'Cari'}
            </button>
            {hasFilters && (
              <button
                onClick={clearFilters}
                disabled={loading}
                style={{
                  backgroundColor: C.white, border: `1px solid ${C.border}`,
                  borderRadius: '8px', padding: '9px 12px',
                  fontSize: '13px', color: C.gray700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            backgroundColor: C.errorBg, border: '1px solid #FECACA',
            borderLeft: `4px solid #DC2626`,
            borderRadius: '8px', padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: C.error }}>{UI.errLabel}</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#991B1B' }}>{error}</p>
          </div>
          <button
            onClick={() => void load(page)}
            style={{
              backgroundColor: '#DC2626', color: '#fff', border: 'none',
              borderRadius: '6px', padding: '6px 14px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {UI.retry}
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && data.length === 0 && (
        <>
          <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
          <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: cardShadow }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ padding: '16px 20px', borderBottom: i < 5 ? `1px solid ${C.border}` : 'none', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ height: '14px', width: '60%', borderRadius: '4px', background: `linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                  <div style={{ height: '12px', width: '40%', borderRadius: '4px', background: `linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && data.length === 0 && (
        <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '64px 24px', textAlign: 'center', boxShadow: cardShadow }}>
          <GraduationCap size={48} color={C.gray200} style={{ marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', fontWeight: 700, color: C.gray900, marginBottom: '6px' }}>{UI.emptyTitle}</p>
          <p style={{ fontSize: '14px', color: C.gray500 }}>{UI.emptyBody}</p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              style={{
                marginTop: '16px', backgroundColor: C.yellow, color: C.black,
                border: 'none', borderRadius: '8px', padding: '9px 20px',
                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {lang === 'en' ? 'Clear Filters' : 'Kosongkan Penapis'}
            </button>
          )}
        </div>
      )}

      {/* Educator list */}
      {data.length > 0 && (
        <>
          {/* Showing X-Y of Z */}
          <div style={{ marginBottom: '10px', fontSize: '13px', color: C.gray500 }}>
            {UI.showing} <strong style={{ color: C.gray900 }}>{from}</strong> {UI.to} <strong style={{ color: C.gray900 }}>{to}</strong> {UI.of} <strong style={{ color: C.gray900 }}>{total.toLocaleString()}</strong> {UI.records}
          </div>

          <div
            style={{
              backgroundColor: C.white, borderRadius: '12px',
              border: `1px solid ${C.border}`, overflow: 'hidden',
              boxShadow: cardShadow, opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {data.map((edu, i) => {
              const statusCfg = STATUS_BADGE[edu.employment_status] ?? { bg: C.gray100, text: C.gray500, label: { ms: edu.employment_status, en: edu.employment_status } };
              const name = edu.preferred_name ?? edu.full_name_en ?? edu.full_name_ms ?? '—';
              const typeLang = EMP_TYPE[edu.employment_type];
              const typeLabel = typeLang ? (lang === 'en' ? typeLang.en : typeLang.ms) : edu.employment_type;

              return (
                <div
                  key={edu.id}
                  onClick={() => navigate(`/admin/educators/${edu.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 20px',
                    borderBottom: i < data.length - 1 ? `1px solid ${C.border}` : 'none',
                    cursor: 'pointer',
                    backgroundColor: i % 2 === 0 ? C.white : C.gray50,
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FFF8D6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? C.white : C.gray50)}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      backgroundColor: '#F3F4F6', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', border: `1px solid ${C.border}`,
                    }}
                  >
                    {edu.photo_url
                      ? <img src={edu.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <span style={{ color: C.gray500, fontSize: '14px', fontWeight: 700 }}>
                          {name.charAt(0).toUpperCase()}
                        </span>
                    }
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: C.gray900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name}
                      </p>
                      {edu.is_sped_certified && (
                        <span style={{ fontSize: '11px', color: '#7C3AED', backgroundColor: '#EDE9FE', padding: '2px 8px', borderRadius: '999px', fontWeight: 700 }}>
                          {UI.sped}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: C.gray500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {edu.service_number}
                      {edu.designation ? ` · ${edu.designation}` : ''}
                      {edu.school_name ? ` · ${edu.school_name}` : ''}
                    </p>
                  </div>

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                    {edu.employment_type && (
                      <span style={{ fontSize: '11px', color: C.gray500, backgroundColor: C.gray100, padding: '3px 10px', borderRadius: '999px', fontWeight: 500 }}>
                        {typeLabel}
                      </span>
                    )}
                    <span style={{ fontSize: '11px', fontWeight: 700, color: statusCfg.text, backgroundColor: statusCfg.bg, padding: '3px 10px', borderRadius: '999px' }}>
                      {lang === 'en' ? statusCfg.label.en : statusCfg.label.ms}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
              <button
                onClick={() => void load(page - 1)}
                disabled={page <= 1 || loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  backgroundColor: C.white, border: `1px solid ${C.border}`,
                  borderRadius: '8px', padding: '8px 14px',
                  fontSize: '13px', color: page <= 1 ? C.gray400 : C.gray700,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: '"Inter", sans-serif', fontWeight: 600,
                }}
              >
                <ChevronLeft size={14} />
                {UI.prev}
              </button>
              <span style={{ fontSize: '13px', color: C.gray500, padding: '0 8px' }}>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => void load(page + 1)}
                disabled={page >= totalPages || loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  backgroundColor: C.white, border: `1px solid ${C.border}`,
                  borderRadius: '8px', padding: '8px 14px',
                  fontSize: '13px', color: page >= totalPages ? C.gray400 : C.gray700,
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  fontFamily: '"Inter", sans-serif', fontWeight: 600,
                }}
              >
                {UI.next}
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </div>
  );
}
